import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-use-case-step',
  templateUrl: './use-case-step.component.html',
  styleUrls: ['./use-case-step.component.scss']
})
export class UseCaseStepComponent implements OnInit {
  analyticsStepName: string = AnalyticsUtility.wizardStepName.useCase;
  currentStep: WizardStep = new WizardStep();
  flexLinkUrl: string = "";
  i18nSubtitle: string = "";
  isEnglish: boolean = true;
  isContinueBtnDisabled: boolean = true;
  primaryUse: string = "";
  showFlexLink: boolean = false;
  stepNumber: number = 0;
  subtitleBeginning: string = "";
  subtitleMiddle: string = "";
  subtitleEnd: string = "";
  title: string = "";
  useCaseOptions: WizardOption[] = [];

  constructor(
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private confSvc: ConfigService,
    private analyticsService: AnalyticsService,
    private utilityService: UtilityService) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.UseCase);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);
    this.title = this.confSvc.getString("useCaseTitle");
    this.subtitleBeginning = this.confSvc.getString("wizardStepSubtitleBeginning");
    this.subtitleMiddle = this.confSvc.getString("singleSelectionWizardStepSubtitleMiddle");
    this.subtitleEnd = this.confSvc.getString("singleSelectionWizardStepSubtitleEnd");
    this.i18nSubtitle = this.confSvc.getString("i18nSingleSelectionWizardStepSubtitle");

    this.isEnglish = this.utilityService.getLocaleInfo().isEnglish;

    this.showFlexLink = this.confSvc.getBool("showFlexLink");
    this.flexLinkUrl = this.confSvc.getString("flexLinkUrl");

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);

      this.setUseCaseOptions();
    });
    this.analyticsService.logCurrentPage(this.analyticsStepName);
  }

  setUseCaseOptions(): void {
    this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse)
      .pipe(
        switchMap((primaryUseOptions: WizardOption[]) => {
          const primaryUseTitle = primaryUseOptions[0]?.title ? primaryUseOptions[0].title : "";
          this.primaryUse = primaryUseTitle;

          return this.wizardService.getSelectedWizardOptionsForStep(this.currentStep.name);
        })
      )
      .subscribe((useCaseOptions: WizardOption[]) => {
        this.isContinueBtnDisabled = useCaseOptions.length === 0;

        switch (this.primaryUse) {
          case this.confSvc.getString("primaryUseEducationTitle"):
            this.buildEducationUseCaseOptions(useCaseOptions);
            break;
          case this.confSvc.getString("primaryUseHealthcareTitle"):
            this.buildHealthcareUseCaseOptions(useCaseOptions);
            break;
          default:
            this.buildEnterpriseUseCaseOptions(useCaseOptions);
            break;
        }
      });
  };


  buildEducationUseCaseOptions(selectedOptions: WizardOption[]) {
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseGradeSchoolTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseGradeSchoolTitle"),
        iconFontSet: 'fas',
        iconName: 'Grade1-6.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseGradeSchoolTitle")),
      }));
    }

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseHighSchoolCollegeTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseHighSchoolCollegeTitle"),
        iconFontSet: 'fas',
        iconName: 'Grade6-12.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseHighSchoolCollegeTitle")),
      }));
    }

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseTeachersStaffTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseTeachersStaffTitle"),
        iconFontSet: 'fas',
        iconName: 'Teacher.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseTeachersStaffTitle")),
      }));
    }
  }

  /**
    *  HEALTHCARE OPTIONS
    */
  buildHealthcareUseCaseOptions(selectedOptions: WizardOption[]) {
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseFrontlineWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayHealthcare")))
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseFrontlineWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseFrontlineWorkerHealthcareSubtitle"),
        iconFontSet: 'fas',
        iconName: "frontline.png",
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseFrontlineWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseFrontlineWorkerTooltip")
      }));

    // if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseKioskTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayHealthcare")))
    //   this.useCaseOptions.push(Object.assign(new WizardOption(), {
    //     step: this.currentStep,
    //     title: this.confSvc.getString("useCaseKioskAndSignageTitle"),
    //     subtitle: this.confSvc.getString("useCaseKioskAndSignageSubtitle"),
    //     iconName: 'kiosk.png',
    //     isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseKioskTitle")),
    //     tooltipText: this.confSvc.getString("useCaseKioskTooltip")
    //   }));
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseKioskTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseKioskTitle"),
        subtitle: this.confSvc.getString("useCaseKioskSubtitle"),
        iconName: 'kiosk.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseKioskTitle")),
        tooltipText: this.confSvc.getString("useCaseKioskTooltip")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseSignageTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseSignageTitle"),
        subtitle: this.confSvc.getString("useCaseSignageSubtitle"),
        iconName: 'signage.jpg',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseSignageTitle")),
        tooltipText: this.confSvc.getString("useCaseKioskTooltip")
      }));
    }

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseContactCenterTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayHealthcare")))
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseContactCenterTitle"),
        subtitle: this.confSvc.getString("useCaseContactCenterSubtitle"),
        iconFontSet: 'fas',
        iconName: 'contactcenter.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseContactCenterTitle")),
        tooltipText: this.confSvc.getString("useCaseFrontlineWorkerTooltip")
      }));

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseClinicianTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayHealthcare")))
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseClinicianTitle"),
        subtitle: this.confSvc.getString("useCaseClinicianSubtitle"),
        iconFontSet: 'fas',
        iconName: 'clinician.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseClinicianTitle")),
        tooltipText: this.confSvc.getString("useCaseClinicianTooltip")
      }));

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseNotListedTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayHealthcare")))
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseNotListedTitle"),
        subtitle: this.confSvc.getString("useCaseNotListedSubtitle"),
        iconFontSet: 'fas',
        iconName: 'other.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseNotListedTitle")),
        tooltipText: this.confSvc.getString("useCaseExecutiveWorkerTooltip")
      }));
  }

  /**
    * ENTERPRISE BUILDER
    */
  buildEnterpriseUseCaseOptions(selectedOptions: WizardOption[]) {
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseFrontlineWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseFrontlineWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseFrontlineWorkerSubtitle"),
        iconFontSet: 'fas',
        iconName: "frontline.png",
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseFrontlineWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseFrontlineWorkerTooltip")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseModernWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseModernWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseModernWorkerSubtitle"),
        iconName: 'modern-worker.jpg',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseModernWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseModernWorkerTitle")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseSharedDevicesTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseSharedDevicesTitle"),
        subtitle: this.confSvc.getString("useCaseSharedDevicesSubtitle"),
        iconName: 'shared-devices.jpg',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseSharedDevicesTitle")),
        tooltipText: this.confSvc.getString("useCaseSharedDevicesTitle")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseContactCenterTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseContactCenterTitle"),
        subtitle: this.confSvc.getString("useCaseContactCenterSubtitle"),
        iconFontSet: 'fas',
        iconName: 'contactcenter.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseContactCenterTitle")),
        tooltipText: this.confSvc.getString("useCaseContactCenterTooltip")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseKioskTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseKioskTitle"),
        subtitle: this.confSvc.getString("useCaseKioskSubtitle"),
        iconName: 'kiosk.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseKioskTitle")),
        tooltipText: this.confSvc.getString("useCaseKioskTooltip")
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseSignageTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseSignageTitle"),
        subtitle: this.confSvc.getString("useCaseSignageSubtitle"),
        iconName: 'signage.jpg',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseSignageTitle")),
        tooltipText: this.confSvc.getString("useCaseKioskTooltip")
      }));
    }

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseNotListedTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseNotListedTitle"),
        subtitle: this.confSvc.getString("useCaseNotListedSubtitle"),
        iconFontSet: 'fas',
        iconName: 'other.png',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseNotListedTitle")),
        tooltipText: this.confSvc.getString("useCaseInformationWorkerTooltip")
      }));
    }
    // if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseModernWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
    //   this.useCaseOptions.push(Object.assign(new WizardOption(), {
    //     step: this.currentStep,
    //     title: this.confSvc.getString("useCaseModernWorkerTitle"),
    //     subtitle: this.confSvc.getString("useCaseModernWorkerSubtitle"),
    //     iconName: 'modern-worker.jpg',
    //     isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseModernWorkerTitle")),
    //     tooltipText: this.confSvc.getString("useCaseModernWorkerTitle")
    //   }));
    // }
  }

  onSelect(event: WizardOption): void {
    this.isContinueBtnDisabled = !event.isSelected;
    this.useCaseOptions.map(item => {
      if (item.title !== event.title) {
        item.isSelected = false;
        this.wizardService.removeSelectedWizardOption(item.title);
      } else {
        item.isSelected = event.isSelected;
      }
    });
    if (event.isSelected) {
      this.wizardService.addSelectedWizardOption(event);
    }
  }

  onSkip(): void {
    this.useCaseOptions.forEach((option) => {
      this.wizardService.addSelectedWizardOption(option);
    });
  }
}
